import React from "react";
import { connect } from "react-redux";
import { Redirect, Route, Router, Switch } from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";
import "./App.scss";
import AppLayout from "./components/Layouts/AppLayout/AppLayout";
import Loading from "./components/Loading/Loading";
import ApiErrorModal from "./components/Modals/ApiErrorModal/ApiErrorModal";
import ContactPage from "./containers/ContactPage/ContactPage";
import FaqPage from "./containers/FaqPage/FaqPage";
import HomePage from "./containers/HomePage/HomePage";
import InvalidTokenPage from "./containers/InvalidTokenPage/InvalidTokenPage";
import PayByMeFailedPage from "./containers/PayByMeResultPage/PayByMeFailedPage";
import PayByMeSuccessPage from "./containers/PayByMeResultPage/PayByMeSuccessPage/PayByMeSuccessPage";
import QuestionPage from "./containers/QuestionPage/QuestionPage";
import { ErrorDto } from "./core/models/dtos/error.dto";
import { MeDto } from "./core/models/dtos/me.dto";
import { LoginRequest } from "./core/models/requests/login.request";
import { setApiError } from "./core/services/appService/setApiError/actions";
import { setAppMounted } from "./core/services/appService/setAppMounted/actions";
import { SetAppMountedState } from "./core/services/appService/setAppMounted/types";
import { setMe } from "./core/services/appService/setMe/actions";
import { setPathname } from "./core/services/appService/setPathname/actions";
import { login } from "./core/services/authService/login/actions";
import { LoginState } from "./core/services/authService/login/types";
import { setRemoteConfig } from "./core/services/firebaseService/setRemoteConfig/actions";
import { SetRemoteConfigState } from "./core/services/firebaseService/setRemoteConfig/types";
import axios from "./core/utilities/axios";
import { Errors } from "./core/utilities/errors";
import { Helpers } from "./core/utilities/helpers";
import { history } from "./core/utilities/history";
import { IStore } from "./core/utilities/reducers";
import { router } from "./core/utilities/router";

interface IProps {
  setAppMountedState: SetAppMountedState;
  setAppMounted: (isMounted: boolean) => void;
  setRemoteConfigState: SetRemoteConfigState;
  setRemoteConfig: () => void;
  setPathname: (pathname: string) => void;
  setApiError: (error?: ErrorDto) => void;
  loginState: LoginState;
  login: (request: LoginRequest) => void;
  me?: MeDto;
  setMe: (me?: MeDto) => void;
}

interface IState {
  callbackHistoryUnregister?: VoidFunction;
}

class App extends React.Component<IProps> {
  state: IState = {
    callbackHistoryUnregister: undefined,
  };

  private readonly token;

  constructor(props: IProps) {
    super(props);
    this.setAxiosInterceptor();
    this.props.setRemoteConfig();
    this.token = Helpers.getQueryParam("token");
  }

  componentDidMount() {
    this.setHistoryListener();
    this.props.login({ token: this.token });
    this.props.setAppMounted(true);
  }

  componentDidUpdate(
    prevProps: Readonly<IProps>,
    prevState: Readonly<{}>,
    snapshot?: any
  ) {
    if (
      prevProps.loginState.loading &&
      !this.props.loginState.loading &&
      this.props.loginState.data
    ) {
      if (this.props.me?.termsAcceptanceDate) {
        history.push(router.QUESTION);
      }
    }
  }

  private setAxiosInterceptor(): void {
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        if (Errors.isAuthError(error)) {
          this.props.setMe();
        } else if (Errors.isApiEventError(error)) {
          // skip
        } else {
          this.props.setApiError(Errors.getErrorDtoFromApiError(error));
        }
        return Promise.reject(error);
      }
    );
  }

  private setHistoryListener(): void {
    const callbackHistoryUnregister = history.listen((location: any) => {
      this.props.setPathname(location.pathname);
    });
    this.setState({ callbackHistoryUnregister });
  }

  private isAppLoading(): boolean {
    return (
      !this.props.setAppMountedState.isMounted ||
      this.props.setRemoteConfigState.loading ||
      this.props.loginState.loading
    );
  }

  private isAppVisible(): boolean {
    return !!this.props.me;
  }

  render() {
    return (
      <Router history={history}>
        {this.isAppLoading() ? (
          <Loading className="app-loading" fontSize={80} />
        ) : this.isAppVisible() ? (
          <AppLayout>
            <Switch>
              <Route exact path={router.HOME} component={HomePage} />
              <Route exact path={router.QUESTION} component={QuestionPage} />
              <Route exact path={router.FAQ} component={FaqPage} />
              <Route exact path={router.CONTACT} component={ContactPage} />
              {/* <Route exact path={router.LEADERBOARD} component={LeaderboardPage}/> */}
              <Redirect to={router.HOME} />
            </Switch>
          </AppLayout>
        ) : (
          <AppLayout>
            <Switch>
              <Route
                exact
                path={router.PAY_BY_ME_SUCCESS}
                component={PayByMeSuccessPage}
              />
              <Route
                exact
                path={router.PAY_BY_ME_FAILED}
                component={PayByMeFailedPage}
              />
              <Route
                exact
                path={router.INVALID_TOKEN}
                component={InvalidTokenPage}
              />
              <Redirect to={router.INVALID_TOKEN} />
            </Switch>
          </AppLayout>
        )}
        <ApiErrorModal />
      </Router>
    );
  }

  componentWillUnmount() {
    if (this.state.callbackHistoryUnregister) {
      this.state.callbackHistoryUnregister();
    }
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return bindActionCreators(
    {
      setAppMounted,
      setRemoteConfig,
      setPathname,
      setApiError,
      login,
      setMe,
    },
    dispatch
  );
};
const mapStateToProps = (store: IStore) => {
  return {
    setAppMountedState: store.setAppMounted,
    setRemoteConfigState: store.setRemoteConfig,
    loginState: store.login,
    me: store.setMe.me,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
